import React, { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import { SubmissionError } from 'redux-form';
import { AccessibilityWrapper } from 'legacy-ft-cb';
import shareResourceToClass from 'chameleon-src/components/assign/AssignManageForm/utils/shareResourceToClass';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import SingleAction from 'chameleon/ui-stack/ui/assessments/actions/single-action';
import InfoMessage from 'chameleon/ui-stack/ui/basic/info-message';
import { getMinTimezone } from 'chameleon-src/utility/utils';
import AssignManageForm from '../AssignManageForm';
import useModalTitle from './useModalTitle';
import { toast } from 'chameleon/ui-stack/ui/toast';
import sendTelemetryEvent from './sendTelemetryEvent';
import { warningMessage } from './constants';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';

const ResourceAssign = (props) => {
  const {
    items: resourceItems,
    onSubmit,
    subject,
    client,
    isReassign,
    savedClasses,
    assignButton,
    assignedButton,
    userClasses,
    assignAll,
    unitTitle,
    subunitTitle,
    subunitLabel,
    videoResource: { unit: { unitId } = {} },
    context,
    onResourceDeselect,
    onReset,
    disabled,
  } = props;

  const items = useMemo(
    () => resourceItems.filter(({ isShareable }) => isShareable),
    [resourceItems],
  );
  if (items.length === 0) {
    return null;
  }
  const item = assignAll ? null : items[0];
  const [showAssignModal, setShowAssignModal] = useState(false);
  const sectionsMinTimezone = useRef('America/New_York');
  const modalTitleData = useModalTitle(
    subject.id,
    items,
    unitTitle,
    unitId,
    subunitTitle,
    subunitLabel,
    assignAll,
  );

  const showWarning = !!items.find((i) => i.alreadyAssigned);

  useEffect(() => {
    const sectionTimezones = userClasses
      .map((s) => s.timezone)
      .filter((t) => !!t);
    const minTimezone = getMinTimezone(sectionTimezones);

    if (minTimezone) {
      sectionsMinTimezone.current = minTimezone;
    }
  }, []);

  const toggleAssignModal = () => setShowAssignModal(!showAssignModal);
  const onModalClose = () => {
    setShowAssignModal(false);
    onReset && onReset();
  };

  const submit = async (assignees, startsAt, dueAt, title) => {
    const allSectionIds = assignees.map((a) => a.id);

    const { ok, errors, newAssignmentsNumber } = await shareResourceToClass(
      assignees,
      null,
      items,
      subject.id,
      client,
      startsAt.format(),
      dueAt ? dueAt.format() : null,
      title,
    );

    const notificationElement = `${newAssignmentsNumber} new video assignment${
      newAssignmentsNumber > 1 || newAssignmentsNumber === 0 ? 's' : ''
    } created.`;

    const notificationConfig = {
      actionText: 'View Assignments',
      actionLink: `/${props.subject.id}/assignments?status=all-assigned&type=video`,
    };

    if (ok) {
      onSubmit && onSubmit(allSectionIds);
      toggleAssignModal();
      toast(notificationElement, notificationConfig);

      if (assignAll) {
        sendTelemetryEvent(subject.id, context, items.length, assignees.length);
      }
    } else {
      const submissionErrors = errors.reduce(
        (acc, { fieldName, fieldErrors }) => ({
          ...acc,
          [fieldName]: fieldErrors,
        }),
        {},
      );
      throw new SubmissionError(submissionErrors);
    }
  };

  const assignLabel = isReassign ? 'Reassign' : 'Assign';

  return (
    <Fragment>
      {savedClasses.length > 0
        ? (assignedButton &&
            React.cloneElement(assignedButton, {
              onClick: toggleAssignModal,
            })) || (
            <ButtonWithIcon
              isSuccess
              icon="checkmark"
              iconOrientation="left"
              onClick={toggleAssignModal}
            >
              Assigned
            </ButtonWithIcon>
          )
        : (assignButton &&
            React.cloneElement(assignButton, {
              onClick: toggleAssignModal,
            })) || (
            <AccessibilityWrapper>
              <SingleAction
                onClick={toggleAssignModal}
                tabIndex={0}
                aria-label={assignLabel}
                className="w-auto min-w-min"
                iconName="assign"
                disabled={disabled}
              >
                <span className="item-text">{assignLabel}</span>
              </SingleAction>
            </AccessibilityWrapper>
          )}
      {showAssignModal && (
        <ConfirmDialog
          size="medium"
          contentWithSpacing={false}
          title={modalTitleData.title}
          subTitle={
            <Fragment>
              {modalTitleData.subTitle}
              {modalTitleData.topBar}
            </Fragment>
          }
          onClose={onModalClose}
        >
          {showWarning ? (
            <InfoMessage isWarning>{warningMessage}</InfoMessage>
          ) : null}
          <AssignManageForm
            {...props}
            item={item}
            items={items}
            isAssign
            isResourceForm
            onSubmit={submit}
            onClose={onModalClose}
            sectionsMinTimezone={sectionsMinTimezone.current}
            onResourceDeselect={onResourceDeselect}
            spacingAroundContent="p-6"
          />
        </ConfirmDialog>
      )}
    </Fragment>
  );
};

ResourceAssign.propTypes = {
  savedClasses: PropTypes.array,
  assignButton: PropTypes.element,
  assignedButton: PropTypes.element,
  isAdminOrCoordinator: PropTypes.bool,
  assignAll: PropTypes.bool,
  unitTitle: PropTypes.string,
  subunitTitle: PropTypes.string,
  subunitLabel: PropTypes.string,
  videoResource: PropTypes.object,
  context: PropTypes.oneOf([null, 'subunit', 'unit']),
  onResourceDeselect: PropTypes.func,
  onReset: PropTypes.func,
};

ResourceAssign.defaultProps = {
  savedClasses: [],
  assignButton: null,
  assignedButton: null,
  isAdminOrCoordinator: false,
  assignAll: false,
  subunitTitle: null,
  subunitLabel: null,
  unitTitle: '',
  videoResource: {},
  context: null,
  onResourceDeselect: null,
  onReset: null,
};

export default withApollo(ResourceAssign);
